import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AssignOperatorModal = ({ isOpen, onClose, onAssign, selectedJobs = [] }) => {
  const [selectedOperator, setSelectedOperator] = useState('');
  const [selectedStage, setSelectedStage] = useState('');

  if (!isOpen) return null;

  const operators = [
    {
      id: 'rajesh-kumar',
      name: 'Rajesh Kumar',
      specialization: 'Digital Printing',
      activeJobs: 3,
      status: 'available',
      rating: 4.8,
      experience: '5 years'
    },
    {
      id: 'priya-sharma',
      name: 'Priya Sharma',
      specialization: 'Cutting',
      activeJobs: 2,
      status: 'available',
      rating: 4.9,
      experience: '7 years'
    },
    {
      id: 'amit-patel',
      name: 'Amit Patel',
      specialization: 'Stitching',
      activeJobs: 4,
      status: 'busy',
      rating: 4.7,
      experience: '6 years'
    },
    {
      id: 'sunita-devi',
      name: 'Sunita Devi',
      specialization: 'Digital Printing',
      activeJobs: 1,
      status: 'available',
      rating: 4.6,
      experience: '4 years'
    },
    {
      id: 'vikram-singh',
      name: 'Vikram Singh',
      specialization: 'Cutting',
      activeJobs: 3,
      status: 'available',
      rating: 4.8,
      experience: '8 years'
    }
  ];

  const stageOptions = [
    { value: '', label: 'Select Stage' },
    { value: 'digital-printing', label: 'Digital Printing' },
    { value: 'cutting', label: 'Cutting' },
    { value: 'stitching', label: 'Stitching' }
  ];

  const operatorOptions = operators.map(op => ({
    value: op.id,
    label: `${op.name} (${op.specialization})`,
    disabled: op.status === 'unavailable'
  }));

  const getFilteredOperators = () => {
    if (!selectedStage) return operators;
    
    const stageMap = {
      'digital-printing': 'Digital Printing',
      'cutting': 'Cutting',
      'stitching': 'Stitching'
    };
    
    return operators.filter(op => op.specialization === stageMap[selectedStage]);
  };

  const getSelectedOperatorDetails = () => {
    return operators.find(op => op.id === selectedOperator);
  };

  const handleAssign = () => {
    if (!selectedOperator || !selectedStage) return;
    
    const operatorDetails = getSelectedOperatorDetails();
    onAssign({
      operatorId: selectedOperator,
      operatorName: operatorDetails.name,
      stage: selectedStage,
      jobs: selectedJobs
    });
    
    onClose();
    setSelectedOperator('');
    setSelectedStage('');
  };

  const selectedOperatorDetails = getSelectedOperatorDetails();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001 p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Assign Operator</h2>
              <p className="text-sm text-muted-foreground">
                {selectedJobs.length > 0 
                  ? `Assign operator to ${selectedJobs.length} selected job(s)`
                  : 'Assign operator to production stage'
                }
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            iconSize={20}
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-6">
            {/* Stage Selection */}
            <div>
              <Select
                label="Production Stage"
                options={stageOptions}
                value={selectedStage}
                onChange={setSelectedStage}
                required
              />
            </div>

            {/* Operator Selection */}
            <div>
              <Select
                label="Select Operator"
                options={operatorOptions}
                value={selectedOperator}
                onChange={setSelectedOperator}
                required
                searchable
                disabled={!selectedStage}
              />
            </div>

            {/* Available Operators */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Available Operators</h4>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {getFilteredOperators().map((operator) => (
                  <div
                    key={operator.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-smooth ${
                      selectedOperator === operator.id
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                    } ${operator.status === 'unavailable' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => operator.status !== 'unavailable' && setSelectedOperator(operator.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-foreground">
                            {operator.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h5 className="font-medium text-foreground">{operator.name}</h5>
                          <p className="text-sm text-muted-foreground">{operator.specialization}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-2 mb-1">
                          <Icon name="Star" size={14} color="var(--color-warning)" />
                          <span className="text-sm font-medium text-foreground">{operator.rating}</span>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${
                          operator.status === 'available' ? 'bg-success' :
                          operator.status === 'busy' ? 'bg-warning' : 'bg-error'
                        }`}></div>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Active Jobs:</span>
                        <span className="ml-2 font-medium text-foreground">{operator.activeJobs}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Experience:</span>
                        <span className="ml-2 font-medium text-foreground">{operator.experience}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Operator Details */}
            {selectedOperatorDetails && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3">Assignment Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Operator:</span>
                    <p className="font-medium text-foreground">{selectedOperatorDetails.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Specialization:</span>
                    <p className="font-medium text-foreground">{selectedOperatorDetails.specialization}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Current Workload:</span>
                    <p className="font-medium text-foreground">{selectedOperatorDetails.activeJobs} jobs</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Rating:</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={14} color="var(--color-warning)" />
                      <span className="font-medium text-foreground">{selectedOperatorDetails.rating}</span>
                    </div>
                  </div>
                </div>
                {selectedJobs.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <span className="text-muted-foreground">Jobs to assign:</span>
                    <p className="font-medium text-foreground">{selectedJobs.length} job(s)</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleAssign}
            disabled={!selectedOperator || !selectedStage}
            iconName="UserCheck"
            iconSize={16}
          >
            Assign Operator
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignOperatorModal;